import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import type { Entry } from 'contentful';
import type { BlogPostSkeleton } from '@/lib/contentful';

/** Latest blog articles on the homepage. The posts are the pages earning nearly all
 *  of the site's Search impressions, but nothing linked to them from the home page —
 *  Search Console showed only 2.3% of Googlebot crawls were discovery. The prerenderer
 *  bakes these links into the static HTML, so home-page authority flows to them.
 *  Contentful lib is dynamic-imported to keep its SDK out of the home chunk. */
export function LatestPosts() {
  const [posts, setPosts] = useState<Entry<BlogPostSkeleton>[]>([]);

  useEffect(() => {
    import('@/lib/contentful')
      .then((m) => m.getBlogPosts())
      .then((items) => setPosts(items.slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12 bg-bg-light">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-2">
              Del blog
            </h2>
            <p className="text-text-secondary">
              Guías y consejos sobre envíos a Cuba.
            </p>
          </div>
          <Link
            to="/blog"
            className="hidden sm:flex items-center gap-1 text-sm text-brand font-medium hover:underline"
          >
            Ver todos los artículos
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => {
            const { title, excerpt, category, publishDate, coverImage, slug } = post.fields;
            const imageUrl =
              coverImage && 'fields' in coverImage
                ? `https:${(coverImage as { fields: { file: { url: string } } }).fields.file.url}`
                : '/hero_delivery.webp';
            const formattedDate = publishDate
              ? new Date(publishDate as string).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : '';

            return (
              <article
                key={post.sys.id}
                className="bg-white rounded-[24px] shadow-card overflow-hidden transition-colors group"
              >
                <Link to={`/blog/${slug as string}`} className="block">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={title as string}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 rounded-full bg-brand/10 text-brand text-xs font-medium">
                        {String(category ?? '').trim()}
                      </span>
                      {formattedDate && (
                        <span className="flex items-center gap-1 text-xs text-text-secondary">
                          <Calendar className="w-3 h-3" />
                          {formattedDate}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-text-primary mb-2 line-clamp-2">
                      {title as string}
                    </h3>
                    <p className="text-text-secondary text-sm line-clamp-2">
                      {excerpt as string}
                    </p>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>

        <Link
          to="/blog"
          className="sm:hidden mt-8 flex items-center justify-center gap-1 text-sm text-text-secondary font-semibold"
        >
          Ver todos los artículos
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
